import { useMemo, useState } from 'react';
import { Table, Input, Select, Button, Space, Modal, Form, Tag, message, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useDocumentRecords } from '@/entities/document/api/hooks';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { exportPdfTable, downloadUtf8Csv } from '@/shared/lib/jspdfCyrillic';
import { antTablePagination, antTableScroll } from '@/shared/constants/antTable';
import { DOC_TYPE_FILTER_OPTIONS, DOC_TYPE_OPTIONS } from '@/shared/constants';
import { SearchInput } from '@/shared/ui/SearchInput';
import type { DocumentRecord } from '@/entities/document/model/documentSchema';
import type { Status } from '@/entities/user/model/userSchema';
import { docTypeLabel, REGISTRY_STATUS_FILTER_OPTIONS, statusLabel } from '../constants';
import { docTypeTag, parseRuDate } from '../utils';
import s from './RegistryTab.module.scss';

export function RegistryTab() {
  const { data, isLoading } = useDocumentRecords();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [docType, setDocType] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [added, setAdded] = useState<DocumentRecord[]>([]);
  const [archived, setArchived] = useState<Set<number>>(new Set());
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const rows = useMemo(() => {
    const all = [...(data ?? []), ...added];
    return all.map((r) => (archived.has(r.id) ? { ...r, status: 'err' as Status } : r));
  }, [data, added, archived]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = periodFrom ? parseRuDate(periodFrom) : null;
    const to = periodTo ? parseRuDate(periodTo) : null;
    return rows.filter((r) => {
      const matchText =
        !q ||
        r.title.toLowerCase().includes(q) ||
        r.details.toLowerCase().includes(q) ||
        r.scope.toLowerCase().includes(q);
      const matchType = docType === 'all' || r.docType === docType;
      const matchStatus = status === 'all' || r.status === status;
      let matchPeriod = true;
      if (from || to) {
        const d = parseRuDate(r.updatedDate);
        if (d) {
          if (from && d < from) matchPeriod = false;
          if (to && d > to) matchPeriod = false;
        }
      }
      return matchText && matchType && matchStatus && matchPeriod;
    });
  }, [rows, search, docType, status, periodFrom, periodTo]);

  const doArchive = (id: number) => {
    setArchived((prev) => new Set(prev).add(id));
    message.success('Документ перемещён в архив');
  };

  const registryColumns: ColumnsType<DocumentRecord> = useMemo(
    () => [
      {
        title: 'Наименование',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
        render: (title: string) => <span className={s['root__link-cell']}>{title}</span>,
      },
      {
        title: 'Тип',
        dataIndex: 'docType',
        key: 'type',
        render: (t: DocumentRecord['docType']) => docTypeTag(t),
      },
      { title: 'Реквизиты', dataIndex: 'details', key: 'details', ellipsis: true },
      { title: 'Область', dataIndex: 'scope', key: 'scope' },
      { title: 'Актуализирован', dataIndex: 'updatedDate', key: 'updated' },
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        render: (st: Status) => <StatusBadge status={st} label={statusLabel(st)} />,
      },
      {
        title: 'Действия',
        key: 'actions',
        width: 100,
        render: (_: unknown, rec: DocumentRecord) =>
          rec.status === 'ok' ? (
            <Popconfirm
              title="Переместить документ в архив?"
              onConfirm={() => doArchive(rec.id)}
              okText="Да"
              cancelText="Нет"
            >
              <Button
                type="link"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                В архив
              </Button>
            </Popconfirm>
          ) : (
            <Tag>Архив</Tag>
          ),
      },
    ],
    [],
  );

  const submitAdd = () => {
    form.validateFields().then((v) => {
      const rec: DocumentRecord = {
        id: -Date.now(),
        title: v.title as string,
        docType: v.docType as DocumentRecord['docType'],
        details: v.details as string,
        scope: v.scope as string,
        updatedDate: v.updatedDate as string,
        status: 'ok',
      };
      setAdded((prev) => [rec, ...prev]);
      setOpen(false);
      form.resetFields();
      message.success('Документ добавлен в реестр');
    });
  };

  const handleExportPdf = () => {
    void exportPdfTable({
      title: 'Реестр НПА и внутренних документов',
      fileSlug: 'Реестр_документов',
      head: [['#', 'Наименование', 'Тип', 'Реквизиты', 'Область', 'Актуализирован', 'Статус']],
      body: filtered.map((r, i) => [
        i + 1,
        r.title,
        docTypeLabel[r.docType] ?? r.docType,
        r.details,
        r.scope,
        r.updatedDate,
        statusLabel(r.status),
      ]),
    }).then(() => message.success('PDF сохранён'));
  };

  const handleExportCsv = () => {
    downloadUtf8Csv(
      'Реестр_документов_Р7.csv',
      'Наименование;Тип;Реквизиты;Область;Актуализирован;Статус',
      filtered.map((r) => [
        r.title,
        docTypeLabel[r.docType] ?? r.docType,
        r.details,
        r.scope,
        r.updatedDate,
        statusLabel(r.status),
      ]),
    );
    message.success('Файл для Р7 (CSV) сохранён');
  };

  return (
    <>
      <Space wrap className={s.root__filters}>
        <SearchInput
          allowClear
          className={s['root__search-input']}
          placeholder="Поиск по наименованию, реквизитам, области…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          className={s['root__type-select']}
          value={docType}
          onChange={setDocType}
          options={[...DOC_TYPE_FILTER_OPTIONS]}
        />
        <Select
          className={s['root__status-select']}
          value={status}
          onChange={setStatus}
          options={[...REGISTRY_STATUS_FILTER_OPTIONS]}
        />
        <Input
          className={s['root__period-input']}
          placeholder="С (ДД.ММ.ГГГГ)"
          value={periodFrom}
          onChange={(e) => setPeriodFrom(e.target.value)}
          allowClear
        />
        <Input
          className={s['root__period-input']}
          placeholder="По (ДД.ММ.ГГГГ)"
          value={periodTo}
          onChange={(e) => setPeriodTo(e.target.value)}
          allowClear
        />
        <Button type="primary" onClick={() => setOpen(true)}>
          + Добавить документ
        </Button>
        <Button onClick={handleExportPdf}>PDF</Button>
        <Button onClick={handleExportCsv}>Р7</Button>
      </Space>
      <Table
        loading={isLoading}
        dataSource={filtered}
        columns={registryColumns}
        rowKey="id"
        size="small"
        pagination={{ ...antTablePagination }}
        scroll={antTableScroll}
        onRow={(record) => ({
          onClick: (e) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (target.closest('button, a, .ant-popover, .ant-table-row-expand-icon, .ant-table-expand-icon')) return;
            navigate(`/documents/records/${record.id}`);
          },
          className: s['root__clickable-row'],
        })}
      />
      <Modal
        title="Новый документ"
        open={open}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        onOk={submitAdd}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Наименование" rules={[{ required: true, message: 'Укажите наименование' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="docType" label="Тип" rules={[{ required: true, message: 'Выберите тип' }]}>
            <Select options={[...DOC_TYPE_OPTIONS]} />
          </Form.Item>
          <Form.Item name="details" label="Реквизиты" rules={[{ required: true, message: 'Укажите реквизиты' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="scope" label="Область" rules={[{ required: true, message: 'Укажите область' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="updatedDate" label="Дата" rules={[{ required: true, message: 'Укажите дату' }]}>
            <Input placeholder="ДД.ММ.ГГГГ" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
